"use client";

import { useState, useTransition } from "react";
import { submitCheckin } from "@/app/actions/checkins";
import { useSpotUser } from "@/app/_components/conditions/SpotUserProvider";
import type { CrowdLevel, CheckinSummary } from "@/lib/conditions/check-in-types";
import {
  crowdLevelLabel,
  crowdLevelEmoji,
} from "@/lib/conditions/check-in-display";

const LEVELS: CrowdLevel[] = ["empty", "quiet", "moderate", "busy", "packed"];

function levelColor(level: CrowdLevel): string {
  switch (level) {
    case "empty":
      return "bg-emerald-100 text-emerald-800 border-emerald-200 hover:bg-emerald-200";
    case "quiet":
      return "bg-lime-100 text-lime-800 border-lime-200 hover:bg-lime-200";
    case "moderate":
      return "bg-amber-100 text-amber-800 border-amber-200 hover:bg-amber-200";
    case "busy":
      return "bg-orange-100 text-orange-800 border-orange-200 hover:bg-orange-200";
    case "packed":
      return "bg-red-100 text-red-800 border-red-200 hover:bg-red-200";
  }
}

function relativeTime(iso: string): string {
  const mins = Math.round(
    (Date.now() - new Date(iso).getTime()) / (1000 * 60)
  );
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  return `${hrs}h ago`;
}

export function CheckInButton({
  locationId,
  summary,
}: {
  locationId: string;
  summary: CheckinSummary;
}) {
  const { isLoggedIn } = useSpotUser();
  const [open, setOpen] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isPending, startTransition] = useTransition();

  const handleSelect = (level: CrowdLevel) => {
    setError(null);
    startTransition(async () => {
      const result = await submitCheckin(locationId, level);
      if (result.success) {
        setDone(true);
        setOpen(false);
      } else {
        setError(result.error ?? "Something went wrong");
      }
    });
  };

  return (
    <div className="bg-white rounded-[16px] border border-border-light p-5 md:p-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h3 className="text-[12px] font-semibold uppercase tracking-[1.2px] text-text-muted mb-1">
            Crowd report
          </h3>
          {summary.totalRecent > 0 ? (
            <p className="text-[13.5px] text-text-body">
              <span className="font-semibold text-navy">
                {summary.totalRecent} check-in
                {summary.totalRecent !== 1 ? "s" : ""}
              </span>{" "}
              in the last 3 hours
              {summary.averageLevel && (
                <>
                  {" "}
                  · reporting{" "}
                  <span className="font-semibold">
                    {crowdLevelEmoji(summary.averageLevel)}{" "}
                    {crowdLevelLabel(summary.averageLevel).toLowerCase()}
                  </span>
                </>
              )}
            </p>
          ) : (
            <p className="text-[13px] text-text-muted">
              No recent check-ins. Be the first to report.
            </p>
          )}
          {summary.lastCheckinAt && (
            <p className="text-[11px] text-text-muted/60 mt-0.5">
              Last report {relativeTime(summary.lastCheckinAt)}
            </p>
          )}
        </div>

        {!done && (
          <button
            onClick={() => {
              if (!isLoggedIn) {
                setError("Log in to check in");
                return;
              }
              setOpen(!open);
            }}
            className="shrink-0 px-4 py-2 rounded-full bg-navy text-white text-[12px] font-semibold hover:bg-navy-hover transition-colors"
          >
            I&apos;m here
          </button>
        )}

        {done && (
          <span className="shrink-0 px-4 py-2 rounded-full bg-emerald-100 text-emerald-800 text-[12px] font-semibold">
            Checked in
          </span>
        )}
      </div>

      {error && (
        <p className="text-[12px] text-red-600 mt-2">{error}</p>
      )}

      {open && (
        <div className="mt-4 pt-4 border-t border-border-light">
          <p className="text-[12px] font-semibold text-text-muted mb-2.5">
            How busy is it?
          </p>
          <div className="flex flex-wrap gap-2">
            {LEVELS.map((level) => (
              <button
                key={level}
                disabled={isPending}
                onClick={() => handleSelect(level)}
                className={`rounded-full border px-3.5 py-1.5 text-[12px] font-semibold transition-colors disabled:opacity-50 ${levelColor(level)}`}
              >
                {crowdLevelEmoji(level)} {crowdLevelLabel(level)}
              </button>
            ))}
          </div>
        </div>
      )}

      {summary.totalRecent >= 3 && (
        <div className="mt-4 pt-3 border-t border-border-light">
          <div className="flex gap-1 h-[24px] items-end">
            {LEVELS.map((level) => {
              const count = summary.distribution[level];
              const pct =
                summary.totalRecent > 0
                  ? (count / summary.totalRecent) * 100
                  : 0;
              const barColor =
                level === "empty" || level === "quiet"
                  ? "bg-emerald-300"
                  : level === "moderate"
                    ? "bg-amber-300"
                    : level === "busy"
                      ? "bg-orange-300"
                      : "bg-red-300";
              return (
                <div
                  key={level}
                  className="flex-1 flex flex-col items-center gap-0.5"
                >
                  <div
                    className={`w-full rounded-t-[2px] ${barColor} transition-all`}
                    style={{ height: `${Math.max(pct, 4)}%` }}
                    title={`${crowdLevelLabel(level)}: ${count}`}
                  />
                  <span className="text-[8px] text-text-muted">
                    {crowdLevelLabel(level).slice(0, 3)}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
